import OTPVerification from '@components/common/OTPVerification';
import endpoints from '@config/endpoints';
import { errorLogger } from '@utils/applicationLogUtils';
import axios from '@utils/axiosExt';
import { decodeWithKeyAndShift, encodeWithKeyAndShift } from '@utils/base64Utils';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Auth from '@utils/auth';
enum OTPRequestTypeCode {
    newOTP = "new_otp",
    resendOTP = "resend_otp",
    OTPTemplateCode = "mobile_number_verification_otp"
}
interface ModalProps {
    showModal: boolean
    handleClose: () => void
    handleSubmit: (value: number) => void
    oldMobileNumber: string;
    showOnlyTextFieldModal: boolean;
}
const MobileNumberOTPVerification: React.FC<ModalProps> = ({
    showModal,
    handleClose,
    handleSubmit,
    oldMobileNumber,
    showOnlyTextFieldModal = false
}) => {
    const [resetKey, setResetKey] = useState<number>(0);
    const [showSpinner, setSHowSpinner] = useState<boolean>(false);
    const [inputFieldShowSpinner, setInputFieldShowSpinner] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [retryMessage, setRetryMessage] = useState<string>('');
    const [retry, setRetry] = useState<number>(3);
    const [isTimeFinished, setIsTimeFinished] = useState<boolean>(false);
    const [otpExpiryTime, setOtpExpiryTime] = useState<number>(90);
    const [otpTransactionId, setOtpTransactionId] = useState<string>('');
    const [mobileNumber, setMobileNumber] = useState<string>('');
    const [textFieldIsError, setTextFieldIsError] = useState<boolean>(false);
    const [textFieldErrorMessage, setTextFieldErrorMessage] = useState<string>("");
    const [showOTPComponent, setShowOTPComponent] = useState<boolean>(false);
    const onOTPSubmit = async (value: number) => {
        setSHowSpinner(true);
        const payload = {
            "otpTransactionId": otpTransactionId,
            "otp": value
        }
        const encodedData = encodeWithKeyAndShift(JSON.stringify(payload));
        try {
            const res = await axios.post(`${endpoints.verifyingOTP}`, { data: encodedData })
            const decodedData = JSON.parse(decodeWithKeyAndShift(res?.data?.data));
            if (res?.status === 200 && decodedData?.data[0].otpVerificationStatus === "success") {
                setSHowSpinner(false);
                handleSubmit(Number(mobileNumber));
                setShowError(false);
                toast.success("OTP verification successful.", { position: "top-center" });
            }
            else {
                setSHowSpinner(false);
                setShowError(true);
            }
        }
        catch (e: any) {
            setSHowSpinner(false);
            const decodedError = JSON.parse(decodeWithKeyAndShift(e?.response?.data?.data));
            setShowError(true);
            if (decodedError?.data[0].message === "invalid_otp") {
                setErrorMessage("Entered Incorrect OTP.")
            }
            else if (decodedError?.data[0].message === "timed_out") {
                setErrorMessage("OTP Expired. Please resend OTP.")
            }
            else if (decodedError?.data[0].message === "exceeded_max_retry_count") {
                setErrorMessage("Exceeded maximum no of OTP retries.")
            }
            else {
                setErrorMessage("Invalid OTP. Please resend OTP.")
            }
        }
    }
    const generateOTP = async (mobileNumber: string, otpRequestTypeCode: string, otpTransactionId: string) => {
        const payload = {
            otpTransactionId: otpTransactionId,
            otpRequestTypeCode: otpRequestTypeCode,
            mobileNumber: mobileNumber,
            otpTemplateCode: OTPRequestTypeCode.OTPTemplateCode,
            isSimulatorMode: true
        };
        const encodedData = encodeWithKeyAndShift(JSON.stringify(payload));
        try {
            const res = await axios.post(`${endpoints.generateOTP}`, { data: encodedData })
            const decodedData = JSON.parse(decodeWithKeyAndShift(res?.data?.data));
            setOtpTransactionId(decodedData?.data[0].otpTransactionId);
            setOtpExpiryTime(decodedData?.data[0].otpExpiryTime);
            return true;
        }
        catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error)
            setSHowSpinner(false);
            toast.error("Error in generating OTP. Please try again.", { position: "top-center" });
            return false;
        }
    }
    const checkMobileNumberExists = async (mobileNumber: string) => {
        try {
            const data = { userName: mobileNumber };
            const encodedPayload = encodeWithKeyAndShift(JSON.stringify(data));
            const response = await axios.post(`${endpoints.checkMobileNumberExists}`, { data: encodedPayload }, Auth.loggedInWithJSON());
            const decodedResponse = JSON.parse(decodeWithKeyAndShift(response?.data?.data?.data));
            return decodedResponse?.data[0]?.exists_flag ?true : decodedResponse?.data[1]?.id?true:false;
        }
        catch (error: any) {
            errorLogger(error?.response?.data || error?.response || error)
            setInputFieldShowSpinner(false);
            toast.error("Error in verifying mobile number. Please try again.", { position: "top-center" });
            throw error;
        }
    }
    const generateOTPForFirstTime = async (value: string) => {
        try {
            if (value === oldMobileNumber) {
                setTextFieldIsError(true);
                setTextFieldErrorMessage("New mobile number cannot be the same as the old one.");
                return false;
            }
            setInputFieldShowSpinner(true);
            const mobileNumberExists = await checkMobileNumberExists(value);
            setInputFieldShowSpinner(false);
            if (mobileNumberExists) {
                setTextFieldIsError(true);
                setTextFieldErrorMessage("Mobile number already registered with a different user.");
                return false;
            }
            setTextFieldIsError(false);
            setTextFieldErrorMessage("");
            // Logic to generate OTP for the first time
            const result = showOnlyTextFieldModal ? false : await generateOTP(value, OTPRequestTypeCode.newOTP, '');
            setMobileNumber(value);
            showOnlyTextFieldModal && handleSubmit(Number(value));
            return result;
        }
        catch (error) {
            setInputFieldShowSpinner(false);
            return false;
        }

    }
    const restartTimer = async () => {
        setShowError(false);
        if (retry <= 0) {
            handleClose();
            toast.error("You have reached the maximum limit to resend OTP. Please try again later.", { position: "top-center" });
        }
        else {
            setSHowSpinner(true);
            await generateOTP(mobileNumber, OTPRequestTypeCode.resendOTP, otpTransactionId);
            setResetKey(prevKey => prevKey + 1);
            setIsTimeFinished(false);
            setRetry(retry - 1);
            setRetryMessage('');
            setSHowSpinner(false);
        }
    }
    return (
        <>
            <OTPVerification
                showModal={showModal}
                handleClose={handleClose}
                inputFieldSpinner={inputFieldShowSpinner}
                handleInputFieldSubmit={generateOTPForFirstTime}
                labelNameForTextField={'Enter New Mobile Number'}
                lengthOfTextField={10}
                otpLabel={'An OTP has been sent to your mobile number ' + mobileNumber}
                onOTPSubmit={onOTPSubmit}
                otpLength={6}
                otpExpiryTime={otpExpiryTime}
                otpRestartTimer={restartTimer}
                otpResetKey={resetKey}
                otpShowSpinner={showSpinner}
                showError={showError}
                setShowError={setShowError}
                otpErrorMessage={errorMessage}
                otpRetryMessage={retryMessage}
                otpRetry={retry}
                isTimeFinished={isTimeFinished}
                setIsTimeFinished={setIsTimeFinished}
                textFieldErrorMessage={textFieldErrorMessage}
                textFieldIsError={textFieldIsError}
                setTextFieldIsError={setTextFieldIsError}
                showOTPComponent={showOTPComponent}
                setShowOTPComponent={setShowOTPComponent}
            />
        </>
    );
};

export default MobileNumberOTPVerification;
